import { httpClient } from '@shared/api';
import type { ChunkMeta, Message, Thread } from '../model/types';

/**
 * Все chat-эндпоинты бэка richat-next (Г.2).
 * Соответствует richat_backend/api/routers/chat.py.
 *
 * Префикс /chat/ (без s) — НЕ путать с legacy /chats/.
 */
export const chatApi = {
  /** POST /chat/threads — создать новый тред. */
  async createThread(assistantId: string): Promise<Thread> {
    const response = await httpClient.post<Thread>('/chat/threads', {
      assistant_id: assistantId,
    });
    return response.data;
  },

  /** GET /chat/threads?assistant_id=... — список тредов (DESC по created_at). */
  async listThreads(assistantId: string): Promise<Thread[]> {
    const response = await httpClient.get<Thread[]>('/chat/threads', {
      params: { assistant_id: assistantId },
    });
    return response.data;
  },

  /** GET /chat/threads/{id}/messages?limit=N — история (ASC по created_at). */
  async getMessages(threadId: string, limit = 100): Promise<Message[]> {
    const response = await httpClient.get<Message[]>(
      `/chat/threads/${threadId}/messages`,
      { params: { limit } },
    );
    return response.data;
  },

  /**
   * POST /chat/threads/{id}/messages — отправить и получить ответ.
   *
   * Синхронный, ждём ~2-10 сек. Таймаут поднимаем до 60 сек на случай
   * долгого retrieval'а или медленного провайдера.
   */
  async sendMessage(threadId: string, content: string, modelId?: number): Promise<Message> {
    const response = await httpClient.post<Message>(
      `/chat/threads/${threadId}/messages`,
      // V2-4: model_id — другая модель только для этого ответа тест-чата (Р42).
      modelId ? { content, model_id: modelId } : { content },
      { timeout: 60_000 },
    );
    return response.data;
  },

  /** DELETE /chat/threads/{id} — удалить тред со всеми сообщениями. */
  async deleteThread(threadId: string): Promise<void> {
    await httpClient.delete(`/chat/threads/${threadId}`);
  },

  /**
   * GET /knowledge/chunks?ids=u1,u2,... — метаданные чанков для citations.
   *
   * ЗАВИСИМОСТЬ: mini-PR на бэке. Если endpoint ещё не применён — будет 404,
   * фронт обработает это gracefully (см. citationsResolver в features/test-chat).
   *
   * Возвращает только метаданные: source_id, source_name, page_number.
   * Текст чанка (content) намеренно НЕ запрашиваем — для плашек он не нужен,
   * экономим payload.
   */
  async getChunksMeta(ids: string[]): Promise<ChunkMeta[]> {
    if (ids.length === 0) return [];
    const response = await httpClient.get<ChunkMeta[]>('/knowledge/chunks', {
      params: { ids: ids.join(',') },
    });
    return response.data;
  },
};
